import React, { useContext, useMemo, useState } from 'react';
import { BinContext } from '../../../../../context/BinContext';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { ContainerContext } from '../../../../../context/ContainerContext';
import { CustomFieldContext } from '../../../../../context/CustomFieldContext';
import { ProductTransactionContext } from '../../../../../context/ProductTransactionContext';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import {
  CreateAndProcessProductTransactionResponse,
  CreateAndProcessProductTransactionVariables,
  ProductTransactionMutations,
} from '../../../../../graphql/productTransaction.graphql';
import useMutation from '../../../../../hooks/useMutation';
import { useToast } from '../../../../../providers/ToastProvider';
import { Container } from '../../../../../types/container';
import { ProductTransaction, ProductTransactionType } from '../../../../../types/productTransaction';
import { CustomFieldEntityType } from '../../../../../types/custom_field';
import { SystemStockLocation } from '../../../../../types/stock_location/stockLocation';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { ContainerUtil } from '../../../../../util/containers.util';
import { Button, CancelButton } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { Divider } from '../../../../components/Divider';
import { DropdownSearchSelect, DropdownSearchSelectById } from '../../../../components/Dropdown';
import { ActionModal } from '../../../../components/Modal';
import { TextInput } from '../../../../components/TextInput';
import { ToastType } from '../../../../components/Toast';
import Icon, { IconType } from '../../../../icons/Icon';
import ProductTransactionCustomFields from '../../../Operation/Order/Modals/Panes/Views/Common/ProductTransactionCustomFields';
import MoveContainerWarningBanner from './MoveContainerWarningBanner';
import { System } from '../../../../../types/system';

function getProcessTextAndIcon(type: ProductTransactionType) {
  switch (type) {
    case ProductTransactionType.inbound:
      return {
        processText: t(TranslationKey.inbound).singular.upper,
        processIcon: IconType.arrowDown,
      };
    case ProductTransactionType.move:
      return {
        processText: t(TranslationKey.move).singular.upper,
        processIcon: IconType.switchVertical1,
      };
    case ProductTransactionType.outbound:
      return {
        processText: t(TranslationKey.outbound).singular.upper,
        processIcon: IconType.arrowUp,
      };
    default:
      return {
        processText: t(TranslationKey.process).singular.upper,
        processIcon: undefined,
      };
  }
}

interface ContainerQuickActionModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  container?: Container;
}

export default function ContainerQuickActionModal({ open, setOpen, container }: ContainerQuickActionModalProps) {
  const { showToast } = useToast();

  const { productTransactions, setProductTransactions } = useContext(ProductTransactionContext);
  const { currentCompany } = useContext(CompanyContext);
  const { containers } = useContext(ContainerContext);
  const { customFields } = useContext(CustomFieldContext);

  const [input, setInput] = useState(
    new ProductTransaction({
      companyId: currentCompany.id,
      product: {
        quantity: 1,
        containerId: container?.id,
        fromStockLocationId: container?.stockLocationId,
        fromBinId: container?.binId,
        fromContainerId: container?.parentId,
      },
      type: (() => {
        if (!container) return undefined;
        if (container.stockLocationId === SystemStockLocation.id) return ProductTransactionType.inbound;
        return ProductTransactionType.outbound;
      })(),
      status: System.ProductTransactionStatus.createdId,
    }),
  );

  const { processText, processIcon } = useMemo(() => getProcessTextAndIcon(input.type), [input]);

  const [process, { loading }] = useMutation<
    CreateAndProcessProductTransactionResponse,
    CreateAndProcessProductTransactionVariables
  >(ProductTransactionMutations.createAndProcess, {
    onCompleted: response => {
      const transaction = new ProductTransaction(response.createAndProcessTransaction[0]);
      setProductTransactions(new Map(productTransactions).set(transaction.id, transaction));
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.productTransactionProcessedSuccessfullyToastTitle).singular.upper,
      });
      setOpen(false);
    },
  });

  const handleProcess = () => {
    process({ variables: { productTransactions: [input.forCreate()] } });
  };

  const validContainers = [...containers.values()];
  const selectedContainer = containers.get(input.product.containerId || '');
  const isOutbound = input.type === ProductTransactionType.outbound || input.type === ProductTransactionType.move;

  const applicableCustomFields = [...customFields.values()].filter(cf => {
    if (cf.hidden || cf.generated) return false;
    if (cf.entityParentType) return false;
    if (
      cf.entityType !== CustomFieldEntityType.containerTransaction &&
      cf.entityType !== CustomFieldEntityType.productTransaction
    ) {
      return false;
    }

    return true;
  });

  const validate = () => {
    if (!input.type) return false;
    if (!input.product.containerId || !selectedContainer) return false;

    if (input.type === ProductTransactionType.inbound || input.type === ProductTransactionType.move) {
      if (!input.product.toStockLocationId || !input.product.toBinId) return false;
    }

    if (applicableCustomFields.some(cf => cf.mandatory && !input.customFields.get(cf.id)?.value)) return false;

    return true;
  };

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={() => setOpen(false)} disabled={loading} />
      <Button
        disabled={!validate()}
        text={processText}
        trailing={processIcon && <Icon type={processIcon} />}
        onClick={handleProcess}
        loading={loading}
      />
    </div>
  );

  return (
    <ActionModal title={t(TranslationKey.stockAdjustment).singular.upper} open={open} setOpen={setOpen} footer={footer}>
      <div className='grid gap-4'>
        {isOutbound && <MoveContainerWarningBanner container={selectedContainer} />}
        <DropdownSearchSelect
          items={validContainers}
          value={containers.get(input.product.containerId || '')}
          onSelect={container => {
            input.withContainerId(container?.id);

            if (!container) {
              input.withFromStockLocationId(undefined).withFromBinId(undefined).withFromContainerId(undefined);
              return setInput(input.withType(undefined as any));
            }

            input
              .withFromStockLocationId(container.stockLocationId)
              .withFromBinId(container.binId)
              .withFromContainerId(container.parentId);
            if (container.stockLocationId === SystemStockLocation.id) {
              setInput(input.withType(ProductTransactionType.inbound));
            } else {
              setInput(input.withType(ProductTransactionType.outbound));
            }
          }}
          placeholder={t(TranslationKey.selectContainer).singular.upper}
          label={t(TranslationKey.container).singular.upper}
          textBuilder={item => item.identifier}
        />
        {selectedContainer && (
          <>
            <Divider />
            <ContainerQuickActionLocationView
              input={input}
              setInput={setInput}
              container={selectedContainer}
              showCustomFields={!!applicableCustomFields.length}
            />
          </>
        )}
      </div>
    </ActionModal>
  );
}

interface ContainerQuickActionLocationViewProps {
  input: ProductTransaction;
  setInput: React.Dispatch<React.SetStateAction<ProductTransaction>>;
  container: Container;
  showCustomFields: boolean;
}

function ContainerQuickActionLocationView({
  input,
  setInput,
  container,
  showCustomFields,
}: ContainerQuickActionLocationViewProps) {
  const { productTransactions } = useContext(ProductTransactionContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { bins } = useContext(BinContext);
  const { containers } = useContext(ContainerContext);

  const isInbound = input.type === ProductTransactionType.inbound || input.type === ProductTransactionType.move;
  const isOutbound = input.type === ProductTransactionType.outbound || input.type === ProductTransactionType.move;

  const source = (
    <>
      <div className='grid grid-cols-2 gap-4'>
        <TextInput
          disabled
          label={t(TranslationKey.sourceStockLocation).singular.upper}
          value={stockLocations.get(container.stockLocationId)?.name}
        />
        <TextInput
          disabled
          label={t(TranslationKey.sourceBin).singular.upper}
          value={bins.get(container.binId)?.name}
        />
        {container.parentId && (
          <TextInput
            disabled
            label={t(TranslationKey.parentContainer).singular.upper}
            value={containers.get(container.parentId || '')?.identifier}
          />
        )}
      </div>
      <Checkbox
        value={input.type === ProductTransactionType.move}
        label={'Is move?'}
        onChange={checked =>
          setInput(input.withType(checked ? ProductTransactionType.move : ProductTransactionType.outbound))
        }
      />
    </>
  );

  const validDestinationContainers = useMemo(() => {
    const childContainers = ContainerUtil.getAllChildContainers(containers, container);
    const invalidContainerIds = new Set([...childContainers.map(c => c.id), container.id]);
    return [...containers.values()].filter(
      c =>
        (c.stockLocationId === SystemStockLocation.id || c.stockLocationId === input.product.toStockLocationId) &&
        !invalidContainerIds.has(c.id),
    );
  }, [productTransactions, containers, container, input.product.toStockLocationId]);

  const destinationContainer = containers.get(input.product.toContainerId || '');

  const destination = (
    <div className='grid grid-cols-2 gap-4'>
      <DropdownSearchSelectById
        mandatory
        items={[...stockLocations.values()]}
        onSelect={id => setInput(input.withToStockLocationId(id))}
        label={t(TranslationKey.destinationStockLocation).singular.upper}
        placeholder={t(TranslationKey.selectStockLocation).singular.upper}
        value={input.product.toStockLocationId}
        textBuilder={item => item.name}
      />
      <DropdownSearchSelectById
        mandatory
        disabled={!input.product.toStockLocationId}
        items={[...bins.values()].filter(bin => bin.stockLocationId === input.product.toStockLocationId)}
        onSelect={id =>
          setInput(input.withToBinId(undefined, destinationContainer?.stockLocationId === SystemStockLocation.id))
        }
        label={t(TranslationKey.destinationBin).singular.upper}
        placeholder={t(TranslationKey.selectBin).singular.upper}
        value={input.product.toBinId}
        textBuilder={item => item.name}
      />
      <DropdownSearchSelect
        items={validDestinationContainers}
        disabled={!input.product.toStockLocationId}
        onSelect={item => setInput(input.withToContainer(item))}
        label={t(TranslationKey.destinationContainer).singular.upper}
        placeholder={t(TranslationKey.selectContainer).singular.upper}
        value={destinationContainer}
        textBuilder={item => item.identifier}
      />
    </div>
  );

  return (
    <div className='grid gap-4'>
      {isOutbound && source}
      {isInbound && destination}
      {showCustomFields && (
        <>
          <Divider />
          <ProductTransactionCustomFields transaction={input} setTransaction={setInput} />
        </>
      )}
    </div>
  );
}
